import React from 'react';
import {
  IChoiceDegreeFinder,
  IDegreeFinderPageSettings,
} from '../../../models/choice-degree-finder/IChoiceDegreeFinder';
import ChoiceDegreeFinderList from '../../molecules/choice-degree-finder-list/choice-degree-finder-list';
import { IDropdown } from '../../../models/dropdown/IDropdown';
import SidebarNavigation from '../../molecules/sidebar-navigation/sidebar-navigation';
import { ISidebarNavigation } from '../../../models/sidebar-navigation/ISidebarNavigation';
import Button from '../../atoms/button/button';
import { ButtonStyleType } from '../../../constants/button-style-type';
import { ButtonSizeType } from '../../../constants/button-size-type';
import colorStyle from '../../../styles/utils/variables.module.scss';

export type Props = {
  choiceDegrees: IChoiceDegreeFinder[];
  choiceDegreesProgramType: IDropdown[];
  choiceDegreesAffiliatedUniversities: IDropdown[];
  brandSidebarNavigation: ISidebarNavigation;
  pageHeading: string;
  pageCopyText: string;
  onFacetSearchInputChange: (value: string) => void;
  onFacetProgramTypeChange: (value: string) => void;
  onFacetAffiliatedUniversityChange: (value: string) => void;
  onResetFacetsChange: () => void;
  onLoadNextPageChange: () => void;
  searchInputValue: string;
  selectedProgramType: string;
  selectedAffiliatedUniversity: string;
  nextPageCount: number;
  isNextPageAvailable: boolean;
  isResultLoading: boolean;
  isResetDisabled: boolean;
  degreeFinderPageSettings: IDegreeFinderPageSettings;
};

export default function ChoiceDegreeFinderPage({
  choiceDegrees,
  choiceDegreesProgramType,
  choiceDegreesAffiliatedUniversities,
  brandSidebarNavigation,
  pageHeading,
  pageCopyText,
  onFacetSearchInputChange,
  onFacetProgramTypeChange,
  onFacetAffiliatedUniversityChange,
  searchInputValue,
  selectedProgramType,
  selectedAffiliatedUniversity,
  nextPageCount,
  isNextPageAvailable,
  isResultLoading,
  isResetDisabled,
  onResetFacetsChange,
  onLoadNextPageChange,
  degreeFinderPageSettings,
}: Props) {
  const handleInputChange = (handler: (value: string) => void) => (value: string) => {
    if (handler) {
      handler(value);
    }
  };

  const renderChoiceDegreeFinderList = () => (
    <ChoiceDegreeFinderList
      choiceDegrees={choiceDegrees}
      programTypeDropdownValues={choiceDegreesProgramType}
      affiliatedUniversitiesDropdownValues={choiceDegreesAffiliatedUniversities}
      pageHeading={pageHeading}
      pageCopyText={pageCopyText}
      onSearchInputChange={handleInputChange(onFacetSearchInputChange)}
      onProgramTypeChange={handleInputChange(onFacetProgramTypeChange)}
      onAffiliatedUniversityChange={handleInputChange(onFacetAffiliatedUniversityChange)}
      searchInputValue={searchInputValue}
      selectedProgramType={selectedProgramType}
      selectedAffiliatedUniversity={selectedAffiliatedUniversity}
      isResetDisabled={isResetDisabled}
      onResetFacetsChange={onResetFacetsChange}
      degreeFinderPageSettings={degreeFinderPageSettings}
    />
  );

  return (
    <div className='custom-page-template'>
      <div className='row'>
        {brandSidebarNavigation && (
          <div className='col-lg-3 px-0 px-lg-3'>
            <SidebarNavigation
              sidebarNavigationProps={brandSidebarNavigation}
              sidebarBrandColor={degreeFinderPageSettings.applicationPrimaryColor}
              b2cFlag={degreeFinderPageSettings.b2cFlag}
              pagePath={degreeFinderPageSettings.pagePath}
              locales={degreeFinderPageSettings.locales}
            />
          </div>
        )}
        <div className={`col-lg-${brandSidebarNavigation ? '9' : '12'} py-lg-4 py-0`}>
          {renderChoiceDegreeFinderList()}
          {isNextPageAvailable && (
            <div className='d-flex justify-content-center align-items-center mt-3'>
              <Button
                buttonID='showMoreDegrees'
                buttonAriaLabel='show more degrees'
                buttonName='showMoreDegrees'
                disabled={isResultLoading}
                buttonStyle={ButtonStyleType.Outline}
                buttonSize={ButtonSizeType.Medium}
                buttonRole='button'
                copyColor={degreeFinderPageSettings.applicationPrimaryColor}
                outlineColor={degreeFinderPageSettings.applicationPrimaryColor}
                backgroundColor={colorStyle.white}
                hoverBackgroundColor={degreeFinderPageSettings.hoverBackgroundColor}
                activeBackgroundColor={degreeFinderPageSettings.activeBackgroundColor}
                onClick={onLoadNextPageChange}
              >
                {isResultLoading ? `Loading...` : `Show ${nextPageCount} more`}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
